import { memo, useEffect, useRef, useState } from 'react';
import { FileSearch } from 'lucide-react';
import { thumbnailReads } from './thumbnail-read-queue';

/** The virtual spine owns mounting; each mounted thumbnail owns one source. */
/** `transform`: Turn page draws the page turned while the engine saves the turn (page-turn-queue.ts). */
export const LazyPageThumbnail = memo(function LazyPageThumbnail({
  previewPath,
  label,
  transform,
}: {
  previewPath: string;
  label: string;
  transform?: string;
}) {
  const [loaded, setLoaded] = useState<{ path: string; source: string } | null>(null);
  const hostRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    setLoaded(null);
    const host = hostRef.current;
    const desktop = window.capturePointDesktop;
    if (!host || !desktop) return;
    let disposed = false;
    let cancelRead: (() => void) | undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        cancelRead = thumbnailReads.schedule(async () => {
          if (disposed) return;
          try {
            const source = await desktop.readPagePreview(previewPath, 'thumbnail');
            if (!disposed) setLoaded({ path: previewPath, source });
          } catch {
            // Keep the placeholder on failure. A different source gets a fresh read.
          }
        });
      },
      { rootMargin: '240px 0px' },
    );
    observer.observe(host);
    return () => {
      disposed = true;
      cancelRead?.();
      observer.disconnect();
    };
  }, [previewPath]);
  const source = loaded?.path === previewPath ? loaded.source : null;
  return (
    <span className="thumbnail-preview" ref={hostRef}>
      {source ? (
        <img
          src={source}
          alt={label}
          draggable={false}
          style={transform ? { transform } : undefined}
        />
      ) : (
        <FileSearch size={18} />
      )}
    </span>
  );
});
